import { DatasetResult, ReportTypes, RMap, ModeModel } from '@mode/shared/contract-common';
import { createAction, props } from '@ngrx/store';

export const domainQueriesRequested = createAction(
  '[ReportFilter] Domain Queries Requested',
  props<{
    datasetResults: DatasetResult[];
    filters: ReportTypes.FilterDefinition[];
    model: ModeModel;
  }>()
);

export const notReadyForDomainQuery = createAction(
  '[ReportFilter] Not Ready For Domain Query',
  props<{
    datasetResult: DatasetResult;
    filters: ReportTypes.FilterDefinition[];
    model: ModeModel;
  }>()
);

export const domainQueriesPerformed = createAction(
  '[ReportFilter] Domain Query Created',
  props<{ refIds: string[] }>()
);

/**
 * Doesn't trigger actual loading, just sets the isLoading state
 */
export const loadReportFilters = createAction('[ReportFilter] Load ReportFilters');

export const loadReportFiltersSuccess = createAction(
  '[ReportFilter] Load ReportFilters Success',
  props<{
    resources: ReportTypes.ReportFilterResource[];
  }>()
);

export const loadReportFiltersFailure = createAction(
  '[ReportFilter] Load ReportFilters Failure',
  props<{ error: any }>()
);

export const loadReportFilterOptsSuccess = createAction(
  '[ReportFilter] Load FilterOpts Success',
  props<{ options: RMap<ReportTypes.ReportFilterOptions> }>()
);

export const loadReportFilterOptsFailure = createAction(
  '[ReportFilter] Load FilterOpts Failure',
  props<{ error: Error }>()
);

export const createReportFilter = createAction(
  '[ReportFilter] Create ReportFilter',
  props<{
    reportToken: string;
    accountUsername: string;
    filterField: ReportTypes.ReportFilterField;
  }>()
);

export const createReportFilterSuccess = createAction(
  '[ReportFilter] Create ReportFilter Success',
  props<{ resource: ReportTypes.ReportFilterResource }>()
);

export const createReportFilterFailure = createAction(
  '[ReportFilter] Create ReportFilter Failure',
  props<{ error: any }>()
);

export const destroyReportFilter = createAction('[ReportFilter] Destroy ReportFilter', props<{ token: string }>());

export const destroyReportFilterSuccess = createAction(
  '[ReportFilter] Destroy ReportFilter Success',
  props<{ token: string }>()
);

export const destroyReportFilterFailure = createAction(
  '[ReportFilter] Destroy ReportFilter Failure',
  props<{ token: string; error: any }>()
);

export const updateReportFilter = createAction(
  '[ReportFilter] Update ReportFilter',
  props<{ filter: ReportTypes.FilterDefinition; defaultSelection?: ReportTypes.FilterValueSelection }>()
);

export const updateReportFilterSuccess = createAction(
  '[ReportFilter] Update ReportFilter Success',
  props<{ resource: ReportTypes.ReportFilterResource }>()
);

export const updateReportFilterFailure = createAction(
  '[ReportFilter] Update ReportFilter Failure',
  props<{ error: any }>()
);

export const hideSelectAll = createAction(
  '[ReportFilter] Hide Select All',
  props<{
    token: string;
    hide: boolean;
    selection?: ReportTypes.FilterValueSelection;
    // Persist will save the selection as the new defaults, and also apply all filters
    persist?: boolean;
  }>()
);

export const hideSelectAllSuccess = createAction(
  '[ReportFilter] Hide Select All Success',
  props<{
    resource: ReportTypes.ReportFilterResource;
    selection?: ReportTypes.FilterValueSelection;
    applyFilters: boolean;
  }>()
);

export const hideSelectAllFailure = createAction(
  '[ReportFilter] Hide Select All Failure',
  props<{ token: string; error: any }>()
);

export const updateReportFilterType = createAction(
  '[ReportFilter] Update ReportFilterType',
  props<{
    token: string;
    filterType: ReportTypes.ReportFilterType;
    controlType: ReportTypes.ReportFilterControlType;
    selection: ReportTypes.FilterSelection;
    defaultSelection: ReportTypes.FilterSelection;
    applyFilters: boolean;
  }>()
);

export const updateReportFilterTypeSuccess = createAction(
  '[ReportFilter] Update ReportFilterType Success',
  props<{ resource: ReportTypes.ReportFilterResource; selection: ReportTypes.FilterSelection; applyFilters: boolean }>()
);

export const updateReportFilterTypeFailure = createAction(
  '[ReportFilter] Update ReportFilterType Failure',
  props<{ error: any }>()
);

export const requestUpdateReportFilterDefaults = createAction(
  '[ReportFilter] Request UpdateReportFilterDefaults',
  props<{ selected: RMap<ReportTypes.FilterSelection> }>()
);

export const updateReportFilterDefaults = createAction(
  '[ReportFilter] Update ReportFilterDefaults',
  props<{ defaults: RMap<ReportTypes.FilterSelection> }>()
);

export const updateReportFilterDefaultsSuccess = createAction(
  '[ReportFilter] Update ReportFilterDefaults Success',
  props<{ resources: ReportTypes.ReportFilterResource[] }>()
);

export const updateReportFilterDefaultsFailure = createAction(
  '[ReportFilter] Update ReportFilterDefaults Failure',
  props<{ error: any }>()
);

export const updateReportFilterPosition = createAction(
  '[ReportFilter] Update ReportFilterPosition',
  props<{ source: string; destination: string }>()
);

export const updateReportFilterPositionSuccess = createAction(
  '[ReportFilter] Update ReportFilterPosition Success',
  props<{ resources: ReportTypes.ReportFilterResource[] }>()
);

export const updateReportFilterPositionFailure = createAction(
  '[ReportFilter] Update ReportFilterPosition Failure',
  props<{ error: any }>()
);

export const requestApplyReportFilters = createAction(
  '[ReportFilter] Request ApplyReportFilters',
  props<{
    selected: RMap<ReportTypes.FilterSelection>;
  }>()
);

export const applyReportFilters = createAction(
  '[ReportFilter] Apply ReportFilters',
  props<{
    selected: RMap<ReportTypes.FilterSelection>;
  }>()
);

export const resetReportFilters = createAction(
  '[ReportFilter] Reset ReportFilters',
  props<{
    defaults: {
      [token: string]: ReportTypes.FilterSelection;
    };
  }>()
);

export const filterSelectionChange = createAction(
  '[ReportFilter] ReportFilter Selection Change',
  props<{
    /**
     * Changed filter token
     */
    token: string;
    /**
     * Changed filter selection (single/multi/range)
     */
    selection: ReportTypes.FilterSelection;
  }>()
);

export const showRelevantValues = createAction(
  '[ReportFilter] ReportFilter Show Relevant Values',
  props<{
    token: string;
    showRelevantValues: boolean;
  }>()
);

export const showRelevantValuesSuccess = createAction(
  '[ReportFilter] Show Relevant Values Success',
  props<{
    resource: ReportTypes.ReportFilterResource;
  }>()
);

export const showRelevantValuesFailure = createAction(
  '[ReportFilter] Show Relevant Values Failure',
  props<{ token: string; error: any }>()
);

export const resetReportFilter = createAction(
  '[ReportFilter] Reset ReportFilter',
  props<{ token: string; defaults: ReportTypes.FilterSelection }>()
);

export const filterPanelLoaded = createAction('[ReportFilter] Filter Panel Loaded');

export type Union =
  | ReturnType<typeof domainQueriesRequested>
  | ReturnType<typeof notReadyForDomainQuery>
  | ReturnType<typeof domainQueriesPerformed>
  | ReturnType<typeof loadReportFilters>
  | ReturnType<typeof loadReportFiltersFailure>
  | ReturnType<typeof loadReportFiltersSuccess>
  | ReturnType<typeof loadReportFilterOptsSuccess>
  | ReturnType<typeof loadReportFilterOptsFailure>
  | ReturnType<typeof requestApplyReportFilters>
  | ReturnType<typeof applyReportFilters>
  | ReturnType<typeof createReportFilter>
  | ReturnType<typeof createReportFilterFailure>
  | ReturnType<typeof createReportFilterSuccess>
  | ReturnType<typeof destroyReportFilter>
  | ReturnType<typeof destroyReportFilterFailure>
  | ReturnType<typeof destroyReportFilterSuccess>
  | ReturnType<typeof resetReportFilter>
  | ReturnType<typeof resetReportFilters>
  | ReturnType<typeof updateReportFilter>
  | ReturnType<typeof updateReportFilterFailure>
  | ReturnType<typeof updateReportFilterSuccess>
  | ReturnType<typeof updateReportFilterType>
  | ReturnType<typeof updateReportFilterTypeFailure>
  | ReturnType<typeof updateReportFilterTypeSuccess>
  | ReturnType<typeof updateReportFilterPosition>
  | ReturnType<typeof updateReportFilterPositionFailure>
  | ReturnType<typeof updateReportFilterPositionSuccess>
  | ReturnType<typeof hideSelectAll>
  | ReturnType<typeof hideSelectAllFailure>
  | ReturnType<typeof hideSelectAllSuccess>
  | ReturnType<typeof requestUpdateReportFilterDefaults>
  | ReturnType<typeof updateReportFilterDefaults>
  | ReturnType<typeof updateReportFilterDefaultsFailure>
  | ReturnType<typeof updateReportFilterDefaultsSuccess>
  | ReturnType<typeof filterSelectionChange>
  | ReturnType<typeof showRelevantValues>
  | ReturnType<typeof showRelevantValuesFailure>
  | ReturnType<typeof showRelevantValuesSuccess>
  | ReturnType<typeof filterPanelLoaded>;
